export interface MailBridge {
  domain: string;
  mailBase: string;
  companySlug?: string;
}

const MAIL_OAUTH_STATE_KEY = 'fluxiplanner-mail-oauth-state';
const MAIL_OAUTH_RETURN_KEY = 'fluxiplanner-mail-oauth-return';

export function getMailBridge(domainOverride?: string | null): MailBridge | null {
  if (domainOverride) {
    const domain = normalizeMailDomain(domainOverride);
    if (!domain || !deployedMailBridgeDomains().includes(domain)) return null;
    return { domain, mailBase: `https://mail.${domain}` };
  }

  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.pages.dev') || host === 'localhost' || host === '127.0.0.1') return null;

  const resolved = resolveMailDomainForHost(host);
  if (!resolved?.domain || resolved.domain.endsWith('.pages.dev')) return null;
  if (!deployedMailBridgeDomains().includes(resolved.domain)) return null;

  return {
    domain: resolved.domain,
    mailBase: `https://mail.${resolved.domain}`,
    companySlug: resolved.companySlug,
  };
}

export function getDefaultMailDomain(): string {
  return getMailBridge()?.domain || 'sahastasai.com';
}

export function getAvailableMailDomains(): string[] {
  return deployedMailBridgeDomains();
}

export function normalizeMailDomain(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^mail\./, '')
    .replace(/\/.*$/, '');
}

export function startSahastaMailOAuth(domainInput: string, returnTo = '/settings'): void {
  const bridge = getMailBridge(domainInput);
  if (!bridge) {
    throw new Error('Choose a SahastaMail domain that is connected to Fluxiplanner.');
  }

  const state = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(MAIL_OAUTH_STATE_KEY, state);
  sessionStorage.setItem(MAIL_OAUTH_RETURN_KEY, returnTo.startsWith('/') ? returnTo : '/settings');

  const callback = new URL('/auth/mail/callback', window.location.origin);
  const target = new URL('/', bridge.mailBase);
  target.searchParams.set('flex_return', callback.toString());
  target.searchParams.set('flex_domain', bridge.domain);
  target.searchParams.set('flex_state', state);
  window.location.assign(target.toString());
}

export function consumeSahastaMailOAuthCallback(): { token: string; domain?: string; state?: string; returnTo?: string } | null {
  if (typeof window === 'undefined' || !window.location.hash) return null;
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  const token = params.get('mail_token');
  if (!token) return null;

  const state = params.get('mail_state') || undefined;
  const expectedState = sessionStorage.getItem(MAIL_OAUTH_STATE_KEY);
  const returnTo = sessionStorage.getItem(MAIL_OAUTH_RETURN_KEY) || '/settings';
  sessionStorage.removeItem(MAIL_OAUTH_STATE_KEY);
  sessionStorage.removeItem(MAIL_OAUTH_RETURN_KEY);
  if (expectedState && state !== expectedState) {
    throw new Error('SahastaMail sign-in state did not match. Please try again.');
  }

  const domain = normalizeMailDomain(params.get('mail_domain') || '');
  window.history.replaceState(null, '', '/auth/mail/callback');
  return { token, domain: domain || undefined, state, returnTo };
}

function resolveMailDomainForHost(host: string): { domain: string; companySlug?: string } | null {
  if (host === 'fluxiplanner.com' || host === 'www.fluxiplanner.com') {
    return { domain: 'sahastasai.com', companySlug: 'central' };
  }

  if (host.endsWith('.fluxiplanner.com')) {
    const companySlug = host.slice(0, -'.fluxiplanner.com'.length);
    const domain = fluxCompanyDomains()[companySlug];
    if (!domain) return null;
    return { domain, companySlug };
  }

  if (host.startsWith('flexiplanner.')) {
    return { domain: host.slice('flexiplanner.'.length) };
  }

  return null;
}

function fluxCompanyDomains(): Record<string, string> {
  const configured = (import.meta as any).env.VITE_FLUX_COMPANY_DOMAINS as string | undefined;
  const raw =
    configured ||
    'central:sahastasai.com,sahastasai:sahastasai.com,hydroterrace:hydroterrace.com,sahanavsai:sahanavsai.com,sahasta:sahasta.com,saimithrallc:saimithrallc.com';
  return raw
    .split(',')
    .map(pair => pair.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const [slug, domain] = pair.split(':').map(part => part?.trim().toLowerCase());
      if (slug && domain) acc[slug] = domain;
      return acc;
    }, {});
}

function deployedMailBridgeDomains(): string[] {
  const configured = (import.meta as any).env.VITE_MAIL_BRIDGE_DOMAINS as string | undefined;
  const domains = configured || 'hydroterrace.com,sahastasai.com,sahanavsai.com,sahasta.com';
  return domains
    .split(',')
    .map(domain => domain.trim().toLowerCase())
    .filter(Boolean);
}

export async function requestMailFlexToken(bridge: MailBridge): Promise<string> {
  const response = await fetch(`${bridge.mailBase}/api/auth/flex-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain: bridge.domain }),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.token) {
    const message =
      payload?.error ||
      (response.status === 401
        ? `Sign in at mail.${bridge.domain}, then return to Fluxiplanner.`
        : `Mail app bridge is not available for ${bridge.domain}.`);
    throw new Error(message);
  }
  return payload.token;
}
